import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck,
  Warning,
  Clock,
  CheckCircle,
  Prohibit,
  Plus,
  ArrowRight,
  ArrowsClockwise,
  Download,
  MagnifyingGlass,
  Package,
  Storefront,
  Receipt,
  Factory,
} from "@phosphor-icons/react";

const TABS = [
  { id: "capa", label: "CAPA Register", Icon: Warning },
  { id: "trace", label: "Recall Traceability", Icon: MagnifyingGlass },
  { id: "audit", label: "Audit Pack", Icon: Download },
];

const STATUS_META = {
  open: { color: "bg-amber-100 text-amber-800", label: "Open", Icon: Warning },
  in_progress: { color: "bg-blue-100 text-blue-800", label: "In Progress", Icon: Clock },
  awaiting_verification: { color: "bg-purple-100 text-purple-800", label: "Awaiting Verification", Icon: Clock },
  verified: { color: "bg-emerald-100 text-emerald-800", label: "Verified", Icon: CheckCircle },
  closed: { color: "bg-slate-200 text-slate-700", label: "Closed", Icon: CheckCircle },
  cancelled: { color: "bg-slate-100 text-slate-500", label: "Cancelled", Icon: Prohibit },
};

// ============================ CAPA TAB ============================

function CapaTab() {
  const [capas, setCapas] = useState([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const emptyForm = {
    title: "",
    category: "food_safety",
    severity: "medium",
    description: "",
    assigned_to: "",
    target_date: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/iso/capa", {
        params: statusFilter ? { status: statusFilter } : {},
      });
      setCapas(data);
    } finally {
      setBusy(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post("/iso/capa", form);
      toast.success("CAPA created");
      setCreating(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const transition = async (id, to_status) => {
    try {
      await api.post(`/iso/capa/${id}/transition`, { to_status });
      toast.success(`Moved to ${to_status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const stats = useMemo(() => {
    const s = { total: capas.length, open: 0, in_progress: 0, awaiting_verification: 0, verified: 0, closed: 0 };
    capas.forEach((c) => { s[c.status] = (s[c.status] || 0) + 1; });
    return s;
  }, [capas]);

  return (
    <div className="space-y-6" data-testid="capa-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { key: "total", label: "Total", color: "bg-slate-50 text-slate-700" },
          { key: "open", label: "Open", color: "bg-amber-50 text-amber-700" },
          { key: "in_progress", label: "In Progress", color: "bg-blue-50 text-blue-700" },
          { key: "awaiting_verification", label: "Verifying", color: "bg-purple-50 text-purple-700" },
          { key: "verified", label: "Verified", color: "bg-emerald-50 text-emerald-700" },
          { key: "closed", label: "Closed", color: "bg-slate-100 text-slate-600" },
        ].map((s) => (
          <div key={s.key} className={`rounded-lg p-3 ${s.color}`} data-testid={`capa-stat-${s.key}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{s.label}</div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{stats[s.key] || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3">
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="capa-filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          data-testid="capa-create-btn"
        >
          <Plus size={14} weight="bold" /> New CAPA
        </button>
      </div>

      {creating && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3" data-testid="capa-create-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-9 mt-1" data-testid="capa-title" />
            </div>
            <div>
              <Label className="text-[11px]">Assigned to (email)</Label>
              <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="h-9 mt-1" data-testid="capa-assigned" />
            </div>
            <div>
              <Label className="text-[11px]">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="food_safety">Food safety</SelectItem>
                  <SelectItem value="quality">Quality</SelectItem>
                  <SelectItem value="audit">Audit</SelectItem>
                  <SelectItem value="complaint">Customer complaint</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Target close date</Label>
              <Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="h-9 mt-1" data-testid="capa-target-date" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px]">Description / issue observed</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="capa-description" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setCreating(false); setForm(emptyForm); }} className="h-9 px-3 rounded-md border border-slate-200 text-sm">Cancel</button>
            <button onClick={submit} className="h-9 px-4 rounded-md bg-blue-600 text-white text-sm font-semibold" data-testid="capa-save-btn">Create CAPA</button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Title</th>
              <th className="text-left px-4 py-2">Category</th>
              <th className="text-left px-4 py-2">Severity</th>
              <th className="text-left px-4 py-2">Owner</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="capa-list">
            {busy && (<tr><td colSpan={7} className="p-6 text-slate-400 text-center">Loading…</td></tr>)}
            {!busy && capas.length === 0 && (<tr><td colSpan={7} className="p-6 text-slate-400 text-center">No CAPAs yet.</td></tr>)}
            {capas.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.open;
              const StatusIcon = meta.Icon;
              const nextStatuses = {
                open: ["in_progress", "cancelled"],
                in_progress: ["awaiting_verification", "cancelled"],
                awaiting_verification: ["verified", "in_progress"],
                verified: ["closed"],
                closed: [],
                cancelled: [],
              }[c.status] || [];
              return (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`capa-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{c.title}</div>
                    {c.target_date && <div className="text-[10px] text-slate-400 mt-0.5">Target · {c.target_date}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 capitalize">{(c.category || "").replace(/_/g, " ")}</td>
                  <td className="px-4 py-3">
                    <span className={"px-2 py-0.5 text-[10px] font-semibold rounded uppercase " + (
                      c.severity === "critical" ? "bg-red-100 text-red-700" :
                      c.severity === "high" ? "bg-orange-100 text-orange-700" :
                      c.severity === "medium" ? "bg-amber-100 text-amber-700" :
                      "bg-slate-100 text-slate-600"
                    )}>{c.severity}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{c.assigned_to || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={"inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold " + meta.color}>
                      <StatusIcon size={11} weight="bold" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {nextStatuses.map((n) => (
                        <button
                          key={n}
                          onClick={() => transition(c.id, n)}
                          className="px-2 py-1 rounded border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-100"
                          data-testid={`capa-move-${c.id}-${n}`}
                        >
                          → {(STATUS_META[n] || {}).label || n}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================ TRACEABILITY TAB ============================

function TraceabilityTab() {
  const [batchNo, setBatchNo] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTrace = async () => {
    if (!batchNo.trim()) return toast.error("Enter FG batch #");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/iso/traceability/full", { params: { fg_batch_no: batchNo.trim() } });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trace failed");
    } finally {
      setLoading(false);
    }
  };

  const back = result?.backward;
  const fwd = result?.forward;

  return (
    <div className="space-y-6" data-testid="trace-tab">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Recall / Traceability</div>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-[11px]">Finished-goods Batch #</Label>
            <Input value={batchNo} onChange={(e) => setBatchNo(e.target.value)} placeholder="e.g., FG-RUN-0001-20260701" className="h-10 mt-1" data-testid="trace-batch-input" />
          </div>
          <button
            onClick={runTrace}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="trace-run-btn"
          >
            <MagnifyingGlass size={14} weight="bold" />
            {loading ? "Tracing…" : "Trace"}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">
          Backward → raw batches, GRN, PO, vendor (deterministic). Forward → invoices billing this FG SKU since manufacture date (best-effort by SKU).
        </p>
      </div>

      {back && back.fg_stock && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-5" data-testid="trace-result">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">FG Batch</div>
              <div className="font-mono text-sm mt-1">{back.fg_stock.batch_no}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                {back.fg_stock.qty} · exp {back.fg_stock.expiry_date || "—"}
              </div>
            </div>
            {back.production_run && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Production Run</div>
                <div className="font-mono text-sm mt-1">{back.production_run.code}</div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {back.production_run.recipe_name} · v{back.production_run.recipe_version} · {back.production_run.actual_yield} kg
                </div>
                {back.production_run.yield_variance_pct !== undefined && (
                  <div className="text-[10px] text-slate-500 mt-0.5">Variance {back.production_run.yield_variance_pct}%</div>
                )}
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Vendors Touched</div>
              <div className="text-sm mt-1">
                {(back.vendors_touched || []).length
                  ? back.vendors_touched.join(" · ")
                  : <span className="text-slate-400">—</span>}
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Package size={12} /> Backward — Raw Materials → GRN → PO → Vendor
            </div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-50/50">
                <tr>
                  <th className="text-left px-3 py-2">Ingredient</th>
                  <th className="text-left px-3 py-2">Batch</th>
                  <th className="text-left px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">GRN</th>
                  <th className="text-left px-3 py-2">PO</th>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-left px-3 py-2">Vendor Score</th>
                </tr>
              </thead>
              <tbody>
                {(back.materials || []).map((m, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2">{m.item_name}</td>
                    <td className="px-3 py-2 font-mono">{m.raw_batch?.batch_no || "—"}</td>
                    <td className="px-3 py-2 tabular-nums">{m.consumed_qty} {m.uom}</td>
                    <td className="px-3 py-2 font-mono">{m.grn?.code || "—"}</td>
                    <td className="px-3 py-2 font-mono">{m.po?.code || "—"}</td>
                    <td className="px-3 py-2">{m.vendor?.name || "—"}</td>
                    <td className="px-3 py-2 tabular-nums">{m.vendor?.auto_score ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg bg-white border border-slate-200 overflow-hidden mt-4">
            <div className="px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Receipt size={12} /> Forward — Invoices billing this SKU since manufacture
              {fwd?.matched_product && (
                <span className="ml-2 font-mono text-[10px] text-slate-400">({fwd.matched_product.product_id})</span>
              )}
            </div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-50/50">
                <tr>
                  <th className="text-left px-3 py-2">Invoice #</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(fwd?.invoices || []).length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-slate-400 text-center text-xs">No invoices matched this SKU since manufacture.</td></tr>
                )}
                {(fwd?.invoices || []).map((iv) => (
                  <tr key={iv.invoice_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono">{iv.invoice_number}</td>
                    <td className="px-3 py-2">{iv.invoice_date}</td>
                    <td className="px-3 py-2">{iv.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₹{(iv.total_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 capitalize text-slate-500">{iv.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ AUDIT PACK TAB ============================

function AuditPackTab() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/iso/audit-pack", { from_date: from, to_date: to });
      setPack(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadHTML = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const url = `${API}/api/iso/audit-pack/download?from_date=${from}&to_date=${to}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="audit-tab">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Audit Evidence Pack — ISO 22000</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px]">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 mt-1" data-testid="audit-from" />
          </div>
          <div>
            <Label className="text-[11px]">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 mt-1" data-testid="audit-to" />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={generate}
              disabled={busy}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-blue-600 text-white text-sm font-semibold disabled:opacity-50 flex-1"
              data-testid="audit-generate"
            >
              <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
              {busy ? "Building…" : "Generate"}
            </button>
            <button
              onClick={downloadHTML}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
              data-testid="audit-download"
            >
              <Download size={14} weight="bold" /> HTML
            </button>
          </div>
        </div>
      </div>

      {pack && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-5 space-y-5" data-testid="audit-preview">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Preview</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(pack.kpis || {}).map(([k, v]) => (
              <div key={k} className="rounded-md bg-white p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                <div className="text-lg font-bold text-slate-900 mt-1 tabular-nums">{v}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(pack.buckets || {}).map(([k, v]) => (
              <div key={k} className="rounded-md bg-white p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{k.replace(/_/g, " ")}</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(v).map(([bk, bv]) => (
                    <span key={bk} className="px-2 py-0.5 text-[11px] rounded bg-slate-100 text-slate-700">
                      {bk} <span className="font-bold">{bv}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ MAIN PAGE ============================

export default function IsoPage() {
  const [tab, setTab] = useState("capa");

  return (
    <div className="p-6 space-y-6" data-testid="iso-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-lg bg-blue-100 text-blue-700">
          <ShieldCheck size={20} weight="duotone" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">ISO 22000 Compliance</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            CAPA workflow · Recall traceability · Audit evidence pack
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px " +
              (tab === t.id
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700")
            }
            data-testid={`iso-tab-${t.id}`}
          >
            <t.Icon size={14} weight="bold" />
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === "capa" && <CapaTab />}
        {tab === "trace" && <TraceabilityTab />}
        {tab === "audit" && <AuditPackTab />}
      </div>
    </div>
  );
}
